import { useGetRole } from 'hooks/ui/useGetRole';
import PageContainer from 'components/PageContainer';
import { useBackgroundExports } from 'hooks/data/backgroundExports/useBackgroundExports';
import { BackgroundExportTypes } from 'further-types/background-exports';
import FullPageSuccess from 'components/FullPageSuccess';
import { useState } from 'react';
import FieldRow from 'components/FormElements/FieldRow';
import { FirmSelector } from 'components/FirmSelector';
import { MuiDateRangePicker } from 'components/FormElements/DateRangePicker';
import InvestorSearch from 'components/InvestorSearch';
import AdviserSelect from 'components/AdviserSelect';
import Button from 'components/Button';
import PageContent from 'components/PageContent';
import PageSection from 'components/PageSection';
import { useForm } from 'react-hook-form';
import { FC } from 'react';
import ButtonGroup from 'components/ButtonGroup';
import { CalendarDay } from 'further-ui/utils';

type FormValues = {
  firmId: string | null;
  params: {
    dateRange?: {
      startDate?: Date;
      endDate?: Date;
    };
    investorId?: string;
    adviserId?: string;
  };
};

const CashTransactionsReport: FC = () => {
  const { isSuperAdmin, firmId } = useGetRole();
  const { handleSubmit, setValue, watch } = useForm<FormValues>({
    defaultValues: {
      params: {
        dateRange: {
          startDate: undefined,
          endDate: undefined,
        },
        investorId: undefined,
        adviserId: undefined,
      },
      firmId,
    },
  });
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const selectedFirmId = watch('firmId') ?? undefined;
  const adviserId = watch('params.adviserId');

  const breadcrumbs = [
    { label: 'Dashboard', link: '/' },
    { label: 'Cash transactions report', link: '/', isActive: true },
  ];

  const { createBackgroundExport, isExporting } = useBackgroundExports();

  const handleExport = async (data: FormValues) => {
    const { startDate, endDate } = data.params.dateRange || {};

    createBackgroundExport.mutate({
      exportType: BackgroundExportTypes.CashTransactionsReport,
      firmId: data.firmId || undefined,
      params: {
        ...data.params,
        dateRange:
          startDate || endDate
            ? {
                startDate: startDate && new CalendarDay(startDate),
                endDate: endDate && new CalendarDay(endDate),
              }
            : undefined,
      },
    });

    setShowSuccessScreen(true);
  };

  const heading = 'System Exports: Cash transactions';

  if (showSuccessScreen) {
    return (
      <PageContainer heading={heading} breadcrumbs={breadcrumbs}>
        <FullPageSuccess
          title="Your cash transactions report is being exported"
          additionalText="Your report will be available soon in the My Downloads section below"
          proceedText="Create another report"
          proceedCallback={() => setShowSuccessScreen(false)}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer heading={heading} breadcrumbs={breadcrumbs}>
      <PageContent>
        <form onSubmit={handleSubmit(handleExport)}>
          <PageSection title="Export cash transactions report" withSpacing>
            {isSuperAdmin && (
              <FieldRow centerTitle title="Firm">
                <FirmSelector
                  firmId={watch('firmId') ?? undefined}
                  onChange={(firmId) => setValue('firmId', firmId)}
                  required={false}
                />
              </FieldRow>
            )}

            <FieldRow centerTitle title="Date range (optional)">
              <MuiDateRangePicker
                dateRange={watch('params.dateRange')}
                onDateRangeChange={(dateRange) => {
                  setValue('params.dateRange', dateRange);
                }}
                placeholder="Select date range"
              />
            </FieldRow>

            <FieldRow centerTitle title="Select single investor (optional)">
              <InvestorSearch
                onInvestorSelect={(investorId) =>
                  setValue('params.investorId', investorId)
                }
                selectedInvestorId={watch('params.investorId')}
                firmId={selectedFirmId}
                disabled={!selectedFirmId}
              />
            </FieldRow>

            <FieldRow
              centerTitle
              title="Select adviser (optional)"
              tooltipText="This will only return cash transactions for investors where all of their subscriptions are linked to this adviser"
            >
              <AdviserSelect
                fullWidth
                adviserId={adviserId}
                onChange={(adviser) => {
                  setValue('params.adviserId', adviser?._id);
                }}
                hiddenAdviserIdsFromOptions={adviserId ? [adviserId] : []}
                placeholder="Search by name or email"
                labelRenderer={(adviser) =>
                  `${adviser.fullName ?? ''} (${adviser.email})`
                }
              />
            </FieldRow>

            <ButtonGroup>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                loading={isExporting}
              >
                Export
              </Button>
            </ButtonGroup>
          </PageSection>
        </form>
      </PageContent>
    </PageContainer>
  );
};
export default CashTransactionsReport;
